import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "wouter";
import {
  Home,
  FileText,
  Wallet,
  Headphones,
  Users,
  Settings,
  LayoutDashboard,
  CreditCard,
  MapPin,
  ClipboardList,
  RefreshCw,
  LogOut,
  AlertTriangle,
  ChevronDown,
  UserPlus,
  Building2,
  Upload,
  TrendingUp,
  CalendarDays,
  Mail,
  ShoppingCart,
  Database,
  Network,
  ScrollText,
  UserCog,
  UserCheck,
  ListChecks,
  Percent,
  Megaphone,
  type LucideIcon,
} from "lucide-react";
import { useClerk, useUser } from "@clerk/react";
import { useGetMe } from "@workspace/api-client-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { RoleSwitcher } from "./RoleSwitcher";
import { NotificationBell } from "./NotificationBell";
import { cn } from "@/lib/utils";

type NavLeaf = { label: string; href: string; icon: LucideIcon };
type NavGroup = { label: string; icon: LucideIcon; children: NavLeaf[] };
type NavItem = NavLeaf | NavGroup;

function isGroup(item: NavItem): item is NavGroup {
  return "children" in item;
}

// Matches exact route and nested/detail routes (e.g. /agents active on /agents/5),
// while avoiding false matches like /data-import vs /data-imports.
function pathActive(location: string, href: string): boolean {
  return location === href || location.startsWith(href + "/");
}

export function AppShell({ children }: { children: React.ReactNode }) {
  const { user } = useUser();
  const { signOut } = useClerk();
  const [location] = useLocation();
  const { data: me, isLoading } = useGetMe();

  const role = me?.role || "agent"; // default or derived

  const navItems = useMemo(() => getNavItems(role), [role]);

  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());

  // Auto-expand the group that contains the active route.
  useEffect(() => {
    const active = navItems.find(
      (it) =>
        isGroup(it) && it.children.some((c) => pathActive(location, c.href)),
    );
    if (active && isGroup(active)) {
      setOpenGroups((prev) => {
        if (prev.has(active.label)) return prev;
        const next = new Set(prev);
        next.add(active.label);
        return next;
      });
    }
  }, [location, navItems]);

  const toggleGroup = (label: string) =>
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <span className="text-gray-400">Loading...</span>
      </div>
    );
  }

  // Agent gets mobile-first view (flat bottom-tab nav, no submenus)
  if (role === "agent") {
    const tabs = navItems.filter((it): it is NavLeaf => !isGroup(it));
    return (
      <div className="font-sans min-h-[100dvh] w-full max-w-[480px] mx-auto bg-gray-50 flex flex-col relative shadow-2xl overflow-hidden text-[#0B132B]">
        <header className="bg-white px-5 py-4 flex items-center justify-between sticky top-0 z-20 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <img
              src={`${import.meta.env.BASE_URL}mooya-mark.png`}
              alt="Mooya"
              className="w-8 h-8 object-contain"
            />
            <span className="font-bold text-xl tracking-tight text-[#0B132B]">
              Mooya
            </span>
          </div>
          <div className="flex items-center gap-3">
            <RoleSwitcher />
            <NotificationBell />
            <Avatar
              className="w-8 h-8 border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => signOut()}
            >
              <AvatarImage
                src={user?.imageUrl}
                alt={user?.fullName || "Agent"}
              />
              <AvatarFallback className="bg-[#0B132B] text-white text-xs">
                {me?.firstName?.charAt(0) || "A"}
              </AvatarFallback>
            </Avatar>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto pb-24">{children}</main>

        <nav className="bg-white border-t border-gray-100 px-2 py-2 pb-6 fixed bottom-0 w-full max-w-[480px] z-20 shadow-[0_-4px_20px_-15px_rgba(0,0,0,0.1)]">
          <div className="flex justify-between items-center">
            {tabs.map((link) => {
              const isActive = pathActive(location, link.href);
              const Icon = link.icon;
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  className="flex-1 flex flex-col items-center justify-center gap-1 p-2 rounded-xl transition-colors hover:bg-gray-50 relative"
                >
                  {isActive && (
                    <div className="absolute top-0 w-8 h-1 bg-[#E6007E] rounded-b-full -mt-2"></div>
                  )}
                  <Icon
                    className={`w-6 h-6 ${isActive ? "text-[#0B132B] fill-[#0B132B]" : "text-gray-400"}`}
                  />
                  <span
                    className={`text-[10px] font-medium ${isActive ? "text-[#0B132B]" : "text-gray-400"}`}
                  >
                    {link.label}
                  </span>
                </Link>
              );
            })}
          </div>
        </nav>
      </div>
    );
  }

  // Staff mobile header flattens all leaves into a horizontal scroller.
  const flatLeaves: NavLeaf[] = navItems.flatMap((it) =>
    isGroup(it) ? it.children : [it],
  );

  // Desktop shell for staff roles — navy left sidebar (matches design mockups)
  return (
    <div className="flex h-[100dvh] w-full bg-[#F8FAFC] font-sans text-[#0B132B] overflow-hidden">
      {/* LEFT SIDEBAR (desktop) */}
      <aside className="hidden md:flex w-64 flex-col bg-[#0B132B] text-white flex-shrink-0 shadow-xl z-20">
        <div className="h-16 flex items-center px-6 border-b border-white/10 shrink-0">
          <Link
            href="/home"
            className="font-bold text-xl tracking-tight text-white flex items-center gap-2 cursor-pointer"
          >
            <img
              src={`${import.meta.env.BASE_URL}mooya-mark.png`}
              alt="Mooya"
              className="w-8 h-8 object-contain"
            />
            Mooya
          </Link>
        </div>

        <div className="p-4 flex-1 flex flex-col gap-0.5 overflow-y-auto">
          <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 px-3 mt-2">
            Menu
          </div>

          {navItems.map((item) => {
            if (!isGroup(item)) {
              const isActive = pathActive(location, item.href);
              const Icon = item.icon;
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors group",
                    isActive
                      ? "bg-[#E6007E]/10 text-[#E6007E]"
                      : "text-slate-400 hover:text-white hover:bg-white/5",
                  )}
                >
                  <Icon
                    className={cn(
                      "w-5 h-5",
                      isActive
                        ? "text-[#E6007E]"
                        : "text-slate-400 group-hover:text-white",
                    )}
                  />
                  <span
                    className={cn(
                      "font-medium text-sm",
                      isActive && "font-semibold",
                    )}
                  >
                    {item.label}
                  </span>
                </Link>
              );
            }

            const Icon = item.icon;
            const hasActiveChild = item.children.some((c) =>
              pathActive(location, c.href),
            );
            const isOpen = openGroups.has(item.label) || hasActiveChild;
            return (
              <div key={item.label}>
                <button
                  type="button"
                  onClick={() => toggleGroup(item.label)}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors group",
                    hasActiveChild
                      ? "text-white"
                      : "text-slate-400 hover:text-white hover:bg-white/5",
                  )}
                >
                  <Icon
                    className={cn(
                      "w-5 h-5",
                      hasActiveChild
                        ? "text-[#E6007E]"
                        : "text-slate-400 group-hover:text-white",
                    )}
                  />
                  <span className="font-medium text-sm flex-1 text-left">
                    {item.label}
                  </span>
                  <ChevronDown
                    className={cn(
                      "w-4 h-4 transition-transform",
                      isOpen ? "rotate-180" : "",
                      hasActiveChild ? "text-[#E6007E]" : "text-slate-500",
                    )}
                  />
                </button>
                {isOpen && (
                  <div className="mt-0.5 mb-1 ml-4 pl-3 border-l border-white/10 flex flex-col gap-0.5">
                    {item.children.map((child) => {
                      const isActive = pathActive(location, child.href);
                      const ChildIcon = child.icon;
                      return (
                        <Link
                          key={child.href}
                          href={child.href}
                          className={cn(
                            "flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors group",
                            isActive
                              ? "bg-[#E6007E]/10 text-[#E6007E]"
                              : "text-slate-400 hover:text-white hover:bg-white/5",
                          )}
                        >
                          <ChildIcon
                            className={cn(
                              "w-4 h-4",
                              isActive
                                ? "text-[#E6007E]"
                                : "text-slate-500 group-hover:text-white",
                            )}
                          />
                          <span
                            className={cn(
                              "font-medium text-sm",
                              isActive && "font-semibold",
                            )}
                          >
                            {child.label}
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="p-4 border-t border-white/10 shrink-0 bg-black/10">
          <div className="flex items-center gap-3 px-1 py-1">
            <Avatar className="w-9 h-9 border border-white/20">
              <AvatarImage src={user?.imageUrl} />
              <AvatarFallback className="bg-white/10 text-white text-xs">
                {me?.firstName?.charAt(0) || "U"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white truncate">
                {me?.fullName || user?.fullName || "User"}
              </p>
              <RoleSwitcher />
            </div>
            <button
              onClick={() =>
                signOut({ redirectUrl: import.meta.env.BASE_URL || "/" })
              }
              className="p-2 text-slate-400 hover:text-white transition-colors"
              aria-label="Sign out"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </aside>

      {/* MAIN CONTENT AREA */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Desktop top bar */}
        <header className="hidden md:flex items-center justify-end h-16 px-8 bg-white border-b border-gray-200 shrink-0 z-10">
          <NotificationBell />
        </header>

        {/* Mobile header + nav */}
        <header className="md:hidden bg-white border-b border-gray-200 shrink-0 shadow-sm z-10">
          <div className="h-16 flex items-center justify-between px-4">
            <Link
              href="/home"
              className="font-bold text-lg tracking-tight text-[#0B132B] flex items-center gap-2 cursor-pointer"
            >
              <img
                src={`${import.meta.env.BASE_URL}mooya-mark.png`}
                alt="Mooya"
                className="w-6 h-6 object-contain"
              />
              Mooya
            </Link>
            <div className="flex items-center gap-2">
              <RoleSwitcher />
              <NotificationBell />
              <button
                onClick={() =>
                  signOut({ redirectUrl: import.meta.env.BASE_URL || "/" })
                }
                className="p-2 text-gray-500 hover:text-gray-900"
                aria-label="Sign out"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-1 px-2 pb-2 overflow-x-auto">
            {flatLeaves.map((link) => {
              const isActive = pathActive(location, link.href);
              const Icon = link.icon;
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  className={cn(
                    "flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors",
                    isActive
                      ? "bg-[#E6007E]/10 text-[#E6007E]"
                      : "text-gray-500 hover:text-[#0B132B] hover:bg-gray-50",
                  )}
                >
                  <Icon className="w-4 h-4" />
                  {link.label}
                </Link>
              );
            })}
          </div>
        </header>

        <main className="flex-1 overflow-auto p-4 md:p-8">
          <div className="mx-auto max-w-7xl w-full space-y-8">{children}</div>
        </main>
      </div>
    </div>
  );
}

function getNavItems(role: string): NavItem[] {
  switch (role) {
    case "agent":
      return [
        { label: "Home", href: "/home", icon: Home },
        { label: "Leads", href: "/orders", icon: FileText },
        { label: "Wallet", href: "/wallet", icon: Wallet },
        { label: "Support", href: "/support", icon: Headphones },
        { label: "Marketing", href: "/marketing", icon: Megaphone },
        { label: "Settings", href: "/settings", icon: Settings },
      ];
    case "master_dealer":
      return [
        { label: "Dashboard", href: "/home", icon: LayoutDashboard },
        {
          label: "My Network",
          icon: Users,
          children: [
            { label: "Sub-Agents", href: "/agents", icon: Users },
            {
              label: "Applications",
              href: "/applications",
              icon: ClipboardList,
            },
          ],
        },
        {
          label: "Onboarding",
          icon: UserPlus,
          children: [
            { label: "Invitations", href: "/invitations", icon: Mail },
            {
              label: "Info Sessions",
              href: "/info-sessions",
              icon: CalendarDays,
            },
          ],
        },
        { label: "Orders", href: "/orders", icon: ShoppingCart },
        { label: "Leads", href: "/leads", icon: ListChecks },
        { label: "Marketing", href: "/marketing", icon: Megaphone },
        { label: "Settings", href: "/settings", icon: Settings },
      ];
    case "account_manager":
      return [
        { label: "Dashboard", href: "/home", icon: LayoutDashboard },
        { label: "Agents", href: "/agents", icon: Users },
        {
          label: "Network",
          icon: Network,
          children: [
            {
              label: "Master Dealers",
              href: "/master-dealers",
              icon: Building2,
            },
            { label: "Regions", href: "/regions", icon: MapPin },
          ],
        },
        {
          label: "Onboarding",
          icon: UserPlus,
          children: [
            {
              label: "Agent Reviews",
              href: "/onboarding-reviews",
              icon: UserCheck,
            },
            {
              label: "Onboarding Funnel",
              href: "/onboarding-funnel",
              icon: TrendingUp,
            },
            { label: "Invitations", href: "/invitations", icon: Mail },
            {
              label: "Info Sessions",
              href: "/info-sessions",
              icon: CalendarDays,
            },
            {
              label: "Applications",
              href: "/applications",
              icon: ClipboardList,
            },
          ],
        },
        { label: "Tickets", href: "/tickets", icon: Headphones },
        { label: "Orders", href: "/orders", icon: ShoppingCart },
        { label: "Leads", href: "/leads", icon: ListChecks },
        { label: "Commission", href: "/commission", icon: Percent },
        { label: "Marketing", href: "/marketing", icon: Megaphone },
        { label: "Settings", href: "/settings", icon: Settings },
      ];
    case "admin":
      return [
        { label: "Dashboard", href: "/home", icon: LayoutDashboard },
        { label: "Users & Roles", href: "/users", icon: UserCog },
        { label: "Staff", href: "/staff", icon: UserPlus },
        {
          label: "Onboarding",
          icon: UserPlus,
          children: [
            {
              label: "Agent Reviews",
              href: "/onboarding-reviews",
              icon: UserCheck,
            },
            {
              label: "Applications",
              href: "/applications",
              icon: ClipboardList,
            },
            {
              label: "Onboarding Funnel",
              href: "/onboarding-funnel",
              icon: TrendingUp,
            },
            { label: "Invitations", href: "/invitations", icon: Mail },
            {
              label: "Info Sessions",
              href: "/info-sessions",
              icon: CalendarDays,
            },
          ],
        },
        {
          label: "Network",
          icon: Network,
          children: [
            {
              label: "Master Dealers",
              href: "/master-dealers",
              icon: Building2,
            },
            {
              label: "Account Managers",
              href: "/account-managers",
              icon: Users,
            },
            { label: "Agents", href: "/agents", icon: Users },
            { label: "Regions", href: "/regions", icon: MapPin },
          ],
        },
        { label: "Orders", href: "/orders", icon: ShoppingCart },
        { label: "Leads", href: "/leads", icon: ListChecks },
        { label: "Commission", href: "/commission", icon: Percent },
        {
          label: "Finance",
          icon: CreditCard,
          children: [
            { label: "Payment Runs", href: "/payment-runs", icon: RefreshCw },
            { label: "Transactions", href: "/transactions", icon: FileText },
            {
              label: "Bank Changes",
              href: "/bank-changes",
              icon: AlertTriangle,
            },
            { label: "Wallet", href: "/wallet", icon: Wallet },
          ],
        },
        {
          label: "Support",
          icon: Headphones,
          children: [
            { label: "Tickets", href: "/tickets", icon: Headphones },
            { label: "Disputes", href: "/disputes", icon: AlertTriangle },
          ],
        },
        {
          label: "Operations",
          icon: Database,
          children: [
            { label: "Data Imports", href: "/data-imports", icon: Database },
            { label: "Run Import", href: "/data-import", icon: Upload },
            { label: "Audit Log", href: "/audit-log", icon: ScrollText },
          ],
        },
        { label: "Marketing", href: "/marketing", icon: Megaphone },
        { label: "Settings", href: "/settings", icon: Settings },
      ];
    case "finance":
      return [
        { label: "Dashboard", href: "/home", icon: LayoutDashboard },
        {
          label: "Payments",
          icon: CreditCard,
          children: [
            { label: "Payment Runs", href: "/payment-runs", icon: RefreshCw },
            { label: "Transactions", href: "/transactions", icon: FileText },
          ],
        },
        { label: "Bank Changes", href: "/bank-changes", icon: AlertTriangle },
        { label: "Disputes", href: "/disputes", icon: AlertTriangle },
        { label: "Leads", href: "/leads", icon: ListChecks },
        { label: "Data Import", href: "/data-import", icon: Upload },
        { label: "Marketing", href: "/marketing", icon: Megaphone },
        { label: "Settings", href: "/settings", icon: Settings },
      ];
    case "poc_support":
      return [
        { label: "Dashboard", href: "/home", icon: LayoutDashboard },
        { label: "Tickets", href: "/tickets", icon: Headphones },
        { label: "Disputes", href: "/disputes", icon: AlertTriangle },
        { label: "Agents", href: "/agents", icon: Users },
        { label: "Leads", href: "/leads", icon: ListChecks },
        { label: "Marketing", href: "/marketing", icon: Megaphone },
        { label: "Settings", href: "/settings", icon: Settings },
      ];
    case "operations":
      return [
        { label: "Dashboard", href: "/home", icon: LayoutDashboard },
        { label: "Applications", href: "/applications", icon: ClipboardList },
        {
          label: "Agent Reviews",
          href: "/onboarding-reviews",
          icon: UserCheck,
        },
        { label: "Agents", href: "/agents", icon: Users },
        { label: "Leads", href: "/leads", icon: ListChecks },
        { label: "Marketing", href: "/marketing", icon: Megaphone },
        { label: "Settings", href: "/settings", icon: Settings },
      ];
    default:
      return [{ label: "Home", href: "/home", icon: Home }];
  }
}
